import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Typography, Form, Row, Col, Card, Space, Layout, Divider } from 'antd';

import { IsRole } from '../../utils/helpers/helpers';
import BreadcrumbContainer from '../Breadcrumb';
import CustomButton from '../../components/CustomButton';
import INTERNAL_LINKS from '../../enums/InternalLinks';
import AdditionalDocumentsNatural from '../../pages/AdditionalDocumentsNatural/index';

import './style.scss';
import NaturalPersonFormGeneralSection from './FormSections/general';
import NaturalPersonFormEducationSection from './FormSections/education';
import NaturalPersonFormCertificationsSection from './FormSections/certifications';
import NaturalPersonFormExperienceSection from './FormSections/experience';
import NaturalPersonICFESFormExperienceSection from './FormSections/icfesExperience';

const { Title } = Typography;
const { Content } = Layout;
const breadcrumbItems = [
  {
    text: 'Proveedores y Consultores - Persona Natural',
    to: `${INTERNAL_LINKS.NATURAL_PERSONS}`,
  },
  {
    text: 'Registro de Persona Natural',
  },
];

const NaturalPersonForm = ({
  onFinish,
  initialValues = {},
  loading = false,
  personID = 0,
  readOnly = false,
  checkDocs,
}) => {
  const formRef = useRef(null);
  const [form] = Form.useForm();
  const [superUser] = useState(IsRole(6));
  const location = useLocation();
  const educationRef =
    location.state && Object.keys(location.state)[0] === 'educationRef'
      ? location.state.educationRef
      : null;
  const otherCertificationRef =
    location.state && Object.keys(location.state)[0] === 'otherCertificationRef'
      ? location.state.otherCertificationRef
      : null;
  const workExperienceRef =
    location.state && Object.keys(location.state)[0] === 'workExperienceRef'
      ? location.state.workExperienceRef
      : null;
  const icfesExperienceRef =
    location.state && Object.keys(location.state)[0] === 'icfesExperienceRef'
      ? location.state.icfesExperienceRef
      : null;

  const [lastSave, setLastSave] = useState(false);

  useEffect(() => {
    if (educationRef) {
      educationRef.current.scrollIntoView({ behavior: 'smooth' });
    }
    if (otherCertificationRef) {
      otherCertificationRef.current.scrollIntoView({ behavior: 'smooth' });
    }
    if (workExperienceRef) {
      workExperienceRef.current.scrollIntoView({ behavior: 'smooth' });
    }
    if (icfesExperienceRef) {
      icfesExperienceRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, []);

  const onSelfFinish = async (data) => {
    try {
      await onFinish(data);
      if (lastSave) {
        setLastSave(false);
        checkDocs();
      }
    } catch (e) {
      formRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <Content>
      <BreadcrumbContainer items={breadcrumbItems} />
      <Title className={'text-uppercase'} level={2}>
        Registro de proveedores y consultores - Persona natural
      </Title>
      <Divider />
      <Form
        name="nest-messages"
        form={form}
        layout="vertical"
        onFinish={readOnly ? () => {} : onSelfFinish}
        onFinishFailed={() =>
          formRef.current.scrollIntoView({ behavior: 'smooth' })
        }
        initialValues={{ ...initialValues }}
        scrollToFirstError={true}
        onValuesChange={(data) => {
          if (data.hasOwnProperty('notificacion_pais')) {
            form.setFieldsValue({
              notificacion_estado: null,
              notificacion_ciudad: null,
            });
          }
          if (data.hasOwnProperty('notificacion_estado')) {
            form.setFieldsValue({
              notificacion_ciudad: null,
            });
          }
        }}
        autoComplete="off"
      >
        <NaturalPersonFormGeneralSection
          readOnly={readOnly}
          initialValues={initialValues}
          divRef={formRef}
        />
        {!readOnly && (
          <>
            <Row gutter={12}>
              <Col span={24}>
                <Space>
                  <CustomButton
                    loading={loading}
                    text="Guardar identificación general"
                  />
                  {superUser && (
                    <CustomButton
                      type={'default'}
                      className={'back-button'}
                      htmlType="button"
                      text={
                        <Link to={INTERNAL_LINKS.NATURAL_PERSONS}>Volver</Link>
                      }
                    />
                  )}
                </Space>
              </Col>
            </Row>
            <br />
          </>
        )}
        {personID > 0 && (
          <>
            <NaturalPersonFormEducationSection
              readOnly={readOnly}
              personID={personID}
              divRef={educationRef}
            />
            <NaturalPersonFormCertificationsSection
              readOnly={readOnly}
              personID={personID}
              divRef={otherCertificationRef}
            />
            <NaturalPersonFormExperienceSection
              readOnly={readOnly}
              personID={personID}
              divRef={workExperienceRef}
              loading={loading}
            />
            <NaturalPersonICFESFormExperienceSection
              readOnly={readOnly}
              personID={personID}
              divRef={icfesExperienceRef}
              loading={loading}
            />
            <AdditionalDocumentsNatural
              person_id={personID}
              readOnly={readOnly}
            />
            {!readOnly && (
              <Card size={'small'} bordered={false}>
                <Space>
                  <CustomButton
                    text="Guardar y terminar registro"
                    loading={loading}
                    onClick={() => {
                      if (!superUser) setLastSave(true);
                      form.submit();
                    }}
                    htmlType="button"
                  />
                  <CustomButton
                    type={'default'}
                    className={'back-button'}
                    htmlType="button"
                    text={<Link to={INTERNAL_LINKS.HOME}> Volver </Link>}
                  />
                </Space>
              </Card>
            )}
          </>
        )}
      </Form>
    </Content>
  );
};
export default NaturalPersonForm;
